import { UnprocessableError } from "../../error/taxonomy.js";
export const SIP_TRANSPORT = ["udp", "tcp", "tls"];
export const SIP_MEDIA_ENCRYPTION = ["none", "srtp_optional", "srtp_required"];
export const WIRE_NUMBER_FORMAT = ["e164_plus", "e164", "international_00", "national"];
export const CALLER_ID_PRESENTATION = ["number_e164_plus", "number_national", "account_name"];
export const SIP_CODEC_POLICY = ["wideband_first", "alaw_only"];
export const SIP_TRUNK_STATE = ["unverified", "verified", "disabled"];
export const SIP_CODEC_NAME = ["G722", "PCMU", "PCMA", "AMR-WB"];
export const SIP_INBOUND_DELIVERY = ["direct", "registration"];
/** IC-79's registration states (the type is the port's, `SipRegistrationState`), as a closed list for readers. */
export const SIP_REGISTRATION_STATE = ["registered", "trying", "failed", "unregistered"];
export const OUTBOUND_ORIGIN = ["on_demand", "test_call", "campaign"];
const COUNTRY_CODE_PATTERN = /^[A-Z]{2}$/;
/** Two upper-case letters. Says nothing about whether the country is reviewed for anything. */
export function isCountryCode(value) {
    return COUNTRY_CODE_PATTERN.test(value);
}
// ── Limits (R-TRK-4) ────────────────────────────────────────────────────────────────────────
export const SIP_TRUNK_LIMIT = Object.freeze({
    displayNameMaxLength: 80,
    hostMaxLength: 253,
    hostLabelMaxLength: 63,
    authUsernameMaxLength: 128,
    passwordMaxLength: 128,
    numberMaxCount: 50,
    allowedAddressMaxCount: 32,
    allowedDestinationCountryMaxCount: 30,
    /** The shortest IPv4 prefix an allowed address may carry (`/16`). */
    ipv4PrefixMin: 16,
    /** The shortest IPv6 prefix an allowed address may carry (`/32`). */
    ipv6PrefixMin: 32,
});
/**
 * The create and patch body fields that make up a trunk's outbound half. A body names all of them or none
 * (`required` on each one missing); `password` is write-only.
 */
export const SIP_TRUNK_OUTBOUND_FIELD = ["host", "port", "auth_username", "password", "allowed_destination_country"];
/** Whether a trunk (or its settings) is inbound-only: derived from `host === null`, never stored apart (0122). */
export function isInboundOnlySipTrunk(trunk) {
    return trunk.host === null;
}
/**
 * The outbound half of a trunk, or `null` for an inbound-only one. A row with some outbound details and not the
 * others cannot exist (0122's `sip_trunk_outbound_all_or_none`); one that does anyway is drift, and throws.
 */
export function sipTrunkOutboundOf(trunk) {
    const { host, port, authUsername, secretRef } = trunk;
    if (host === null && port === null && authUsername === null && secretRef === null)
        return null;
    if (host === null || port === null || authUsername === null || secretRef === null) {
        throw new RangeError("sipTrunkOutboundOf: a SIP trunk holds some outbound details and not the others");
    }
    return Object.freeze({ host, port, authUsername, secretRef });
}
/**
 * The plain words of `422 sip_trunk_inbound_only` (design §28.4, §28.5.8): what was refused, and the outbound
 * details the trunk lacks, so the member knows what to add.
 */
export function sipTrunkInboundOnlyMessage(subject) {
    const missing = "It has no outbound server address, port, username or password from your provider";
    switch (subject) {
        case "dial":
            return `This SIP trunk only receives calls, so wetalk cannot call through it. ${missing}. Add them to the trunk first. Nothing was dialled.`;
        case "check":
            return `Nothing to check: calls arrive from your provider. This SIP trunk only receives calls — ${missing.charAt(0).toLowerCase()}${missing.slice(1)} for wetalk to try.`;
        case "caller_id":
            return `This SIP trunk only receives calls, so its numbers present no caller ID. ${missing}. Add them to the trunk first.`;
    }
}
/** `422 sip_trunk_inbound_only`: a dial, a check or a caller-ID number refused on an inbound-only trunk, before anything. */
export class SipTrunkInboundOnlyError extends UnprocessableError {
    subject;
    constructor(subject, field) {
        super({ code: "sip_trunk_inbound_only", message: sipTrunkInboundOnlyMessage(subject), ...(field === undefined ? {} : { field }) });
        this.subject = subject;
    }
}
// ── The SIP leg key (the room a SIP leg lives in, DD8-31) ──────────────────────────────────
const OUTBOUND_SIP_LEG_KEY = /^sip-out_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/** `sip-in_<did-wire>_<guid>` — the `callee` rule with `room_prefix: "sip-in"` and `randomize: true`. */
const INBOUND_SIP_LEG_KEY = /^sip-in_[0-9A-Za-z+]{1,32}_[0-9A-Za-z]{1,64}$/;
/** The outbound room the runtime names and creates before it dials: `sip-out_<conversation_id>`. */
export function outboundSipLegKey(conversationId) {
    const key = `sip-out_${conversationId}`;
    if (!OUTBOUND_SIP_LEG_KEY.test(key)) {
        throw new RangeError("outboundSipLegKey: the conversation id is not a lower-case UUID");
    }
    return key;
}
/** A room name as a `SipLegKey`, or `null` when it is not one of the two SIP shapes. */
export function sipLegKeyOf(raw) {
    return OUTBOUND_SIP_LEG_KEY.test(raw) || INBOUND_SIP_LEG_KEY.test(raw) ? raw : null;
}
/** Which way a SIP leg runs, read from its key alone. */
export function sipLegDirectionOf(key) {
    return key.startsWith("sip-out_") ? "outbound" : "inbound";
}
// ── The inbound route's invariants (IC-71) ─────────────────────────────────────────────────
/**
 * Thrown by `SipEdgeObjectPort.upsertInboundRoute` BEFORE any network call when a spec (or the
 * objects it would create) breaks an invariant (ME4). Never a response: the reconciler logs it and
 * leaves the trunk's inbound not ready.
 */
export class SipRouteInvariantError extends Error {
    sipTrunkId;
    invariantBreach;
    constructor(sipTrunkId, invariantBreach) {
        super(`The inbound route of sip_trunk ${sipTrunkId} breaks: ${invariantBreach.join(", ")}`);
        this.name = "SipRouteInvariantError";
        this.sipTrunkId = sipTrunkId;
        this.invariantBreach = Object.freeze([...invariantBreach]);
    }
}
//# sourceMappingURL=sip-trunk.js.map